/* global.js — Pique Vitality theme. Vanilla, progressive enhancement. */
(function () {
  'use strict';

  /* ---- i18n: read localized strings emitted in theme.liquid, substitute [[token]] placeholders ---- */
  const S = (window.theme && window.theme.strings) || {};
  window.theme = window.theme || {};
  window.theme.t = function (str, vars) {
    if (str == null) return '';
    if (!vars) return str;
    return str.replace(/\[\[(\w+)\]\]/g, (m, k) => (k in vars ? vars[k] : m));
  };
  const t = window.theme.t;

  /* ---- Cart drawer ---- */
  // NB: the header cart icon also carries data-cart-drawer (as an on/off flag), so exclude the
  // toggle here or querySelector grabs the icon (first in DOM) instead of the real drawer panel.
  const drawer = document.querySelector('[data-cart-drawer]:not([data-cart-toggle])');
  // cart behaviour comes from theme settings, surfaced as data-attrs on the header cart icon
  const cartToggle = document.querySelector('[data-cart-toggle]');
  const cartDrawerOn = !!drawer && !!cartToggle && cartToggle.dataset.cartDrawer === '1';
  const cartAddAction = (cartToggle && cartToggle.dataset.cartAction) || 'drawer';
  const cartUrl = (cartToggle && cartToggle.getAttribute('href')) || '/cart';
  function openCart(e) { if (e) e.preventDefault(); if (drawer) drawer.setAttribute('aria-hidden', 'false'); }
  function closeCart() { if (drawer) drawer.setAttribute('aria-hidden', 'true'); }

  // Delegated controls so they survive drawer re-renders.
  document.addEventListener('click', (e) => {
    // drawer off → let the cart icon navigate to the cart page (don't preventDefault)
    if (e.target.closest('[data-cart-toggle]')) { if (cartDrawerOn) openCart(e); return; }
    if (e.target.closest('[data-cart-close]')) { closeCart(); }
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeCart(); });

  // Remove a line straight from the drawer (no full page reload).
  // Change a line and re-render in ONE request: /cart/change.js accepts a `sections`
  // param (Section Rendering API) and returns the re-rendered drawer + updated cart in
  // its response — no follow-up fetches. A sequence token drops out-of-order responses
  // from rapid stepper taps so a slow earlier response can't overwrite a newer state.
  let cartChangeSeq = 0;
  async function changeCart(line, quantity) {
    const seq = ++cartChangeSeq;
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity, sections: 'cart-drawer' })
    });
    const cart = await res.json();
    if (seq !== cartChangeSeq) return; // a newer change is in flight — let it apply instead
    if (cart && cart.sections && cart.sections['cart-drawer']) {
      applyCartUpdate(cart.sections['cart-drawer'], cart.item_count);
    } else {
      await refreshCart();
    }
  }

  document.addEventListener('click', async (e) => {
    const rm = e.target.closest('.drawer__remove');
    if (!rm || !drawer || !drawer.contains(rm)) return;
    e.preventDefault();
    const line = rm.closest('[data-line]');
    if (!line) return;
    rm.style.opacity = '.4';
    try {
      await changeCart(parseInt(line.dataset.line, 10), 0);
    } catch (err) { rm.style.opacity = ''; }
  });

  // Quantity steppers inside the drawer — same change + re-render flow as remove.
  async function drawerChangeQty(line, quantity) {
    try {
      await changeCart(line, Math.max(0, quantity));
    } catch (err) {}
  }
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-drawer-qty-up],[data-drawer-qty-down]');
    if (!btn || !drawer || !drawer.contains(btn)) return;
    e.preventDefault();
    const lineEl = btn.closest('[data-line]');
    const input = btn.parentElement.querySelector('[data-drawer-qty-input]');
    if (!lineEl || !input) return;
    const next = Math.max(0, (parseInt(input.value, 10) || 0) + (btn.hasAttribute('data-drawer-qty-up') ? 1 : -1));
    input.value = next;
    drawerChangeQty(parseInt(lineEl.dataset.line, 10), next);
  });
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-drawer-qty-input]');
    if (!input || !drawer || !drawer.contains(input)) return;
    const lineEl = input.closest('[data-line]');
    if (!lineEl) return;
    drawerChangeQty(parseInt(lineEl.dataset.line, 10), parseInt(input.value, 10) || 0);
  });

  // Pack upgrade nudge: swap a line to the bigger pack variant. /cart/change.js can't
  // change a line's variant, so this is add-new-then-remove-old — the add first so the
  // cart never renders empty mid-swap; the remove (by line key, indexes shift after the
  // add) carries the sections param so the drawer re-renders from its response.
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-upsell-swap]');
    if (!btn || !drawer || !drawer.contains(btn)) return;
    e.preventDefault();
    const lineEl = btn.closest('[data-line]');
    if (!lineEl || !lineEl.dataset.key) return;
    btn.disabled = true;
    const qtyInput = lineEl.querySelector('[data-drawer-qty-input]');
    const qty = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
    try {
      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: parseInt(btn.dataset.variant, 10), quantity: qty })
      });
      if (!addRes.ok) throw new Error('upsell add failed');
      const seq = ++cartChangeSeq;
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: lineEl.dataset.key, quantity: 0, sections: 'cart-drawer' })
      });
      const cart = await res.json();
      if (seq === cartChangeSeq && cart && cart.sections && cart.sections['cart-drawer']) {
        applyCartUpdate(cart.sections['cart-drawer'], cart.item_count);
      } else {
        await refreshCart();
      }
    } catch (err) {
      btn.disabled = false;
      await refreshCart(); // recover whatever state the cart is actually in
    }
  });

  /* ---- AJAX add to cart (forms with [data-add-to-cart]) ---- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-add-to-cart]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    // swap only the label span's text — replacing button markup would orphan the cached
    // [data-atc-price]/[data-atc-compare] references product.js writes into
    if (btn) { const tgt = btn.querySelector('[data-atc-label]') || btn; btn.dataset.label = tgt.textContent; tgt.textContent = S.adding || 'Adding…'; btn.disabled = true; }
    try {
      // Ask the add mutation to return the re-rendered drawer in the same response
      // (Section Rendering API) so the drawer opens with fresh contents after ONE
      // round-trip. add.js doesn't return the cart total, so the count badge updates
      // via a background fetch that no longer blocks opening the drawer.
      const fd = new FormData(form);
      fd.append('sections', 'cart-drawer');
      const res = await fetch('/cart/add.js', { method: 'POST', body: fd, headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('add failed');
      const data = await res.json().catch(() => null);
      if (data && data.sections && data.sections['cart-drawer']) {
        applyCartUpdate(data.sections['cart-drawer'], null);
        fetch('/cart.js').then((r) => r.json()).then((c) => applyCartUpdate(null, c.item_count)).catch(() => {});
      } else {
        await refreshCart();
      }
      // post-add behaviour (theme setting): stay put, go to the cart page, or open the drawer
      if (cartAddAction === 'none') { /* stay on the page; the header cart count has refreshed */ }
      else if (cartAddAction === 'cart' || !cartDrawerOn) { window.location.href = cartUrl; }
      else { openCart(); }
    } catch (err) {
      if (btn) (btn.querySelector('[data-atc-label]') || btn).textContent = S.try_again || 'Try again';
    } finally {
      if (btn) { setTimeout(() => { (btn.querySelector('[data-atc-label]') || btn).textContent = btn.dataset.label || S.add_to_cart || 'Add to cart'; btn.disabled = false; }, 600); }
    }
  });

  // Swap in freshly-rendered drawer HTML and/or the header count badge.
  function applyCartUpdate(html, itemCount) {
    if (html && drawer) {
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.drawer__panel');
      const panel = drawer.querySelector('.drawer__panel');
      if (fresh && panel) panel.innerHTML = fresh.innerHTML;
    }
    if (itemCount != null) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = itemCount; });
    }
  }

  // Re-render the drawer body from the server (Section Rendering API) so the
  // freshly-added line, free-shipping bar and subtotal all update instantly.
  // Used by flows whose mutation response doesn't carry the drawer section
  // (e.g. add-to-cart); changeCart() applies straight from its own response.
  async function refreshCart() {
    try {
      const res = await fetch('/?sections=cart-drawer');
      const data = await res.json();
      applyCartUpdate(data && data['cart-drawer'], null);
    } catch (e) {}
    try {
      const cart = await (await fetch('/cart.js')).json();
      applyCartUpdate(null, cart.item_count);
    } catch (e) {}
  }

  /* ---- Announcement rotation ---- */
  document.querySelectorAll('[data-announcement]').forEach((bar) => {
    if (bar.dataset.annLayout === 'columns') return; // static columns — every message shown, no rotation/interval
    const msgs = [...bar.querySelectorAll('[data-ann-msg]')];
    if (msgs.length < 2) return;
    let i = 0;
    const show = (n) => { msgs.forEach((m, k) => m.classList.toggle('is-active', k === n)); };
    bar.querySelector('[data-ann-next]')?.addEventListener('click', () => { i = (i + 1) % msgs.length; show(i); });
    bar.querySelector('[data-ann-prev]')?.addEventListener('click', () => { i = (i - 1 + msgs.length) % msgs.length; show(i); });
    setInterval(() => { i = (i + 1) % msgs.length; show(i); }, 5000);
  });

  /* ---- Accordions ([data-accordion] > [data-acc-trigger]) ---- */
  document.querySelectorAll('[data-acc-trigger]').forEach((t) => {
    t.addEventListener('click', () => {
      const item = t.closest('[data-acc-item]');
      if (item) item.toggleAttribute('open');
    });
  });

  /* ---- Reliable autoplay for in-flow decor videos (image-with-text, hero, image-banner).
     Browsers defer autoplay for videos that start below the fold and don't reliably resume
     on scroll, and iOS may not paint a poster under object-fit — so force play/pause as they
     enter/leave the viewport. Muted playback needs no user gesture. PDP gallery videos are
     excluded (handled by syncGalleryVideos). ---- */
  (function autoplayInViewVideos() {
    const vids = [].slice.call(document.querySelectorAll('video[autoplay]')).filter((v) => !v.closest('[data-gallery-video]'));
    if (!vids.length || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        const v = en.target;
        if (en.isIntersecting) { v.muted = true; const p = v.play(); if (p && p.catch) p.catch(() => {}); }
        else { try { v.pause(); } catch (e) {} }
      });
    }, { threshold: 0.25 });
    vids.forEach((v) => { v.addEventListener('playing', () => v.classList.add('is-playing')); io.observe(v); });
  })();

  /* ---- Product-card variant swatches: pick a variant, then quick-add it ---- */
  document.addEventListener('click', (e) => {
    const sw = e.target.closest('[data-card-swatch]');
    if (!sw) return;
    const group = sw.closest('[data-card-swatches]');
    const body = sw.closest('.card-product__body') || sw.closest('.card-product');
    if (group) group.querySelectorAll('[data-card-swatch]').forEach((s) => s.classList.toggle('is-active', s === sw));
    if (sw.dataset.variantId && body) {
      const idInput = body.querySelector('[data-card-variant-id]');
      if (idInput) idInput.value = sw.dataset.variantId;
      const addBtn = body.querySelector('[data-card-add]');
      if (addBtn) {
        const avail = sw.dataset.available === 'true';
        addBtn.disabled = !avail;
        addBtn.textContent = avail ? (addBtn.dataset.labelAdd || addBtn.textContent) : (addBtn.dataset.labelOut || 'Sold out');
      }
    }
    // swap the card image to the variant's featured image (src AND srcset — the
    // browser keeps picking from a stale srcset if only src changes)
    if (sw.dataset.variantImage) {
      const card = sw.closest('.card-product');
      const img = card && card.querySelector('.card-product__img:not(.card-product__img--hover)');
      if (img && img.getAttribute('src') !== sw.dataset.variantImage) {
        img.src = sw.dataset.variantImage;
        if (sw.dataset.variantSrcset) img.setAttribute('srcset', sw.dataset.variantSrcset);
        else img.removeAttribute('srcset');
      }
    }
  });

  /* ---- Lazy product recommendations ---- */
  const recs = document.querySelector('[data-recommendations]');
  if (recs && recs.dataset.url) {
    fetch(recs.dataset.url)
      .then((r) => r.text())
      .then((html) => {
        const el = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recommendations]');
        if (el) recs.innerHTML = el.innerHTML;
      })
      .catch(() => {});
  }

  /* ---- Autoplay background videos ----
     The autoplay+muted attributes alone are unreliable: some browsers won't start
     a video unless `muted` is set as a PROPERTY and play() is called from script.
     Prime every autoplay <video> (hero, UGC widget, testimonials) and retry on canplay. */
  function primeVideo(v) {
    v.muted = true;            // property, not just attribute — required for muted autoplay
    v.playsInline = true;
    var attempt = function () {
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    };
    attempt();
    // retry once enough data has buffered (covers preload="metadata")
    v.addEventListener('canplay', attempt, { once: true });
    // and when it scrolls into view, in case the browser deferred an offscreen video
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) { attempt(); io.unobserve(e.target); } });
      });
      io.observe(v);
    }
  }
  document.querySelectorAll('video[autoplay]').forEach(primeVideo);

  /* ---- Back-to-top button ---- */
  var toTop = document.querySelector('[data-scrolltop]');
  if (toTop) {
    var toggleTop = function () { toTop.classList.toggle('is-show', window.scrollY > 600); };
    window.addEventListener('scroll', toggleTop, { passive: true });
    toggleTop();
    toTop.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  /* ---- Reveal sections on scroll (html.anim-ready set inline in <head>) ---- */
  if (document.documentElement.classList.contains('anim-ready')) {
    var sections = document.querySelectorAll('#MainContent .shopify-section');
    if ('IntersectionObserver' in window && sections.length) {
      var revealIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { e.target.classList.add('is-in'); revealIO.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px -8% 0px' });
      sections.forEach(function (s) { revealIO.observe(s); });
      // safety net: never leave a section hidden (e.g. if it never triggers)
      setTimeout(function () { sections.forEach(function (s) { s.classList.add('is-in'); }); }, 1600);
    } else {
      sections.forEach(function (s) { s.classList.add('is-in'); });
    }
  }

  /* ---- Theme editor: when a section is re-rendered (you edit a field), it's a fresh
     DOM node with no .is-in, so the reveal CSS would leave it blank. Reveal it and
     re-prime any autoplay videos. (Its own inline scripts re-run with the new HTML.) ---- */
  document.addEventListener('shopify:section:load', function (e) {
    var sec = e.target;
    if (!sec || !sec.classList) return;
    sec.classList.add('is-in');
    if (sec.querySelectorAll) {
      sec.querySelectorAll('.shopify-section').forEach(function (s) { s.classList.add('is-in'); });
      sec.querySelectorAll('video[autoplay]').forEach(primeVideo);
    }
  });
})();
